import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Screen } from "../components/Screen";
import { Logo } from "../brand/Logo";
import { RevealedTruth } from "../brand/RevealedTruth";
import { Button } from "../components/Button";
import { useQuiz } from "../features/quiz/QuizContext";
import { triggerTelegramReadyAfterPaint } from "../telegram/readySignal";
import styles from "./WelcomeScreen.module.css";

// Mirrors DEMO_QUIZ_SIZE from packages/shared/src/config.ts — display copy
// only, not imported at runtime here on purpose: @aletheia/shared's barrel
// re-exports Zod-backed schema/engine modules that only the API needs, and
// pulling any runtime value from it (rather than a `import type`) drags all
// of that into the web bundle (+~13 kB gzip, measured). If DEMO_QUIZ_SIZE
// changes, update both places.
const DEMO_QUIZ_SIZE = 10;

// Minutes-per-question at a comfortable reading/answering pace — used only
// to derive the Welcome screen's time estimate for a DEMO_QUIZ_SIZE session.
const MINUTES_PER_QUESTION = 0.5;

export function WelcomeScreen() {
  const navigate = useNavigate();
  const { startNewTest, phase, errorMessage } = useQuiz();

  // WelcomeScreen is ALETHEIA's initial route — the app's first real screen.
  // Telegram is told the app is ready only from here, once this has
  // actually painted, never earlier (see ../telegram/readySignal.ts).
  useEffect(() => {
    triggerTelegramReadyAfterPaint();
  }, []);

  // Deliberately no /api/pool-size fetch on mount here. The demo session
  // size is a fixed local constant (DEMO_QUIZ_SIZE, not the bank size), so
  // there is nothing this screen needs from the API before the user can
  // press Start — that request was previously fired on every cold load
  // (measured ~600-800ms round trip on a throttled connection) purely to
  // guard against an effectively-impossible "0 published questions"
  // production state. That state is already handled safely by
  // startNewTest()'s own error path (the API returns 409 and it surfaces as
  // a normal recoverable error below) — so the extra request bought no real
  // protection, only startup network chatter.
  async function handleStart() {
    // Only navigate on success — QuizScreen has no error/retry UI of its
    // own (it shows a bare loading spinner for any non-"question" phase),
    // so navigating there after a failed session creation used to strand
    // the user on an unexplained, stuck screen. Staying on Welcome keeps
    // the visible error + re-enabled Start button (the retry action) intact.
    const started = await startNewTest();
    if (started) navigate("/quiz");
  }

  const estimatedMinutes = Math.max(1, Math.round(DEMO_QUIZ_SIZE * MINUTES_PER_QUESTION));

  return (
    <Screen>
      <RevealedTruth className={styles.atmosphere} />
      <div className={styles.wrap}>
        <div className={styles.mark}>
          <Logo size={52} />
        </div>
        <div>
          <h1 className={styles.title}>ALETHEIA</h1>
          <span className={styles.supportLine}>Psychology Knowledge</span>
        </div>
        <p className={styles.tagline}>Проверьте понимание ключевых идей психологии — на основе точных источников.</p>

        <div className={styles.facts}>
          <div className={styles.fact}>
            <span className={styles.factValue}>{DEMO_QUIZ_SIZE} вопросов</span>
            <span className={styles.factLabel}>Объём</span>
          </div>
          <div className={styles.fact}>
            <span className={styles.factValue}>≈ {estimatedMinutes} минут</span>
            <span className={styles.factLabel}>Время</span>
          </div>
        </div>

        {errorMessage && <p className={styles.error}>{errorMessage}</p>}

        <div className={styles.cta}>
          <Button onClick={handleStart} disabled={phase === "loading"}>
            {phase === "loading" ? "Загрузка…" : "Начать тест"}
          </Button>
          {phase === "loading" && <p className={styles.hint}>Подготавливаем вопросы…</p>}
        </div>
      </div>
    </Screen>
  );
}
